import React from 'react';
import { Modal, Descriptions, Tag, Alert, Button, Space, Typography } from 'antd';
import { CheckCircleOutlined, EditOutlined, CloseOutlined } from '@ant-design/icons';
import { ParsedClienteDataDto } from '../../types/facturacion';
import { useResponsive } from '../../hooks/useResponsive';
import { useGetRegimenesFiscalesQuery } from '../../services/facturacionApi';

const { Text } = Typography;

interface QRDataPreviewModalProps {
  visible: boolean;
  parsedData: ParsedClienteDataDto | null;
  onConfirm: (data: ParsedClienteDataDto) => void;
  onEdit: () => void;
  onCancel: () => void;
}

const QRDataPreviewModal: React.FC<QRDataPreviewModalProps> = ({
  visible,
  parsedData,
  onConfirm,
  onEdit,
  onCancel,
}) => {
  const { isMobile } = useResponsive();
  const { data: regimenesFiscales = [] } = useGetRegimenesFiscalesQuery();

  const validateRFC = (rfc: string): boolean => {
    const rfcPattern = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/;
    return rfcPattern.test(rfc);
  };

  const isRFCValid = parsedData ? validateRFC(parsedData.rfc) : false;

  return (
    <Modal
      title={
        <Space>
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
          <span>Datos del QR Detectados</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      destroyOnClose
      maskClosable={false}
      footer={[
        <Button key="cancel" icon={<CloseOutlined />} onClick={onCancel}>
          Cancelar
        </Button>,
        <Button key="edit" icon={<EditOutlined />} onClick={onEdit}>
          Editar Manualmente
        </Button>,
        <Button
          key="confirm"
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={() => parsedData && onConfirm(parsedData)}
          disabled={!isRFCValid}
        >
          Confirmar y Usar
        </Button>,
      ]}
      width={isMobile ? '100%' : 600}
    >
      {parsedData ? (
        <>
          <Alert
            message="Revise los datos extraídos del código QR"
            description="Verifique que la información es correcta antes de continuar. Puede editarla manualmente si es necesario."
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Descriptions
            bordered
            column={1}
            size={isMobile ? 'small' : 'middle'}
            labelStyle={{ fontWeight: 600, width: isMobile ? '40%' : '30%' }}
          >
            <Descriptions.Item label="RFC">
              <Space>
                <Text
                  strong
                  style={{
                    fontFamily: 'monospace',
                    fontSize: isMobile ? 13 : 15,
                  }}
                >
                  {parsedData.rfc}
                </Text>
                {isRFCValid ? (
                  <Tag color="success">Válido</Tag>
                ) : (
                  <Tag color="error">Formato Inválido</Tag>
                )}
              </Space>
            </Descriptions.Item>

            <Descriptions.Item label="Razón Social">
              <Text>{parsedData.razonSocial}</Text>
            </Descriptions.Item>

            {parsedData.regimenFiscal && (
              <Descriptions.Item label="Régimen Fiscal">
                {(() => {
                  const regimen = regimenesFiscales.find((r) => r.clave === parsedData.regimenFiscal);
                  return regimen ? (
                    <Space direction="vertical" size={2}>
                      <Tag color="blue" style={{ fontSize: isMobile ? 12 : 14 }}>
                        {parsedData.regimenFiscal}
                      </Tag>
                      <Text type="secondary" style={{ fontSize: isMobile ? 11 : 13 }}>
                        {regimen.descripcion}
                      </Text>
                    </Space>
                  ) : (
                    <Tag color="blue">{parsedData.regimenFiscal}</Tag>
                  );
                })()}
              </Descriptions.Item>
            )}

            {parsedData.codigoPostal && (
              <Descriptions.Item label="Código Postal">
                <Text>{parsedData.codigoPostal}</Text>
              </Descriptions.Item>
            )}
          </Descriptions>

          {!isRFCValid && (
            <Alert
              message="RFC Inválido"
              description="El RFC extraído del QR no tiene un formato válido. Por favor, edite manualmente o escanee de nuevo."
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          <Alert
            message="Campos Adicionales"
            description="Después de confirmar, podrá completar los campos opcionales como email, teléfono y dirección completa."
            type="info"
            style={{ marginTop: 16 }}
          />
        </>
      ) : (
        <Alert
          message="No hay datos para mostrar"
          type="warning"
          showIcon
        />
      )}
    </Modal>
  );
};

export default QRDataPreviewModal;
